import React, { useState, useEffect, useRef } from 'react';
import TabPane from './tab_pane';
import classNames from 'classnames';
import './tabs.scss';
import debounce from 'lodash/debounce';

interface IProps {
  className?: string;
  style?: object;
  activeKey: number;
  children: any;
  onChange: (key: number) => void;
}

const Tabs = (props: IProps) => {
  const { children, style, className } = props;
  const [activeKey, setActiveKey] = useState(1);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const navRef = useRef<HTMLDivElement>(null);
  const childLen = children ? children.length : 0;

  useEffect(() => {
    if (props.activeKey !== activeKey) {
      setActiveKey(props.activeKey);
    }
  }, [props.activeKey]);

  // 滚动到活动标签
  const scrollToActiveTab = () => {
    if (!navRef.current) return;
    const activeElement = navRef.current.querySelector('.nav-item.active');
    if (!activeElement) return;
    activeElement.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'center',
    });
  };

  // 处理滚动按钮点击
  const handleScroll = (direction: 'left' | 'right') => {
    if (!navRef.current) return;
    const scrollAmount = 150; // 每次滚动150px
    navRef.current.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth',
    });
  };

  const onChange = (key: number) => {
    setActiveKey(key);
    props.onChange(key);
  };

  useEffect(() => {
    const element = navRef.current;
    if (!element) return;
    let isMounted = true;
    const checkScrollPosition = () => {
      if (!isMounted || !element) return;
      const { scrollLeft, scrollWidth, clientWidth } = element;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 1);
    };

    const setupScrollListener = () => {
      if (isMounted && element) {
        element.addEventListener('scroll', checkScrollPosition);
      }
    };

    const cleanupScrollListener = () => {
      if (element) {
        element.removeEventListener('scroll', checkScrollPosition);
      }
    };
    let observer: ResizeObserver | null = null;
    const handleResize = debounce((entries: ResizeObserverEntry[]) => {
      if (!isMounted || !element) return;
      const entry = entries[0];
      if (entry.contentRect.width > 0) {
        checkScrollPosition();
        setupScrollListener();
        observer?.disconnect();
        observer = null;
      }
    }, 100);

    // 立即检查并设置监听器（如果可见）
    if (element.clientWidth > 0) {
      checkScrollPosition();
      setupScrollListener();
    } else {
      // 设置一次性观察器等待元素可见
      observer = new ResizeObserver(handleResize);
      observer.observe(element);
    }

    return () => {
      isMounted = false;
      cleanupScrollListener();
      observer?.disconnect();
      handleResize.cancel();
    };
  }, []);

  useEffect(() => {
    setTimeout(scrollToActiveTab, 100);
  }, [activeKey]);

  return (
    <div className={classNames('rt-tabs', className)} style={style}>
      <div className="tabs-nav-container">
        {/* 左侧滚动按钮 */}
        <button
          className={`scroll-button ${canScrollLeft ? '' : 'disabled'}`}
          onClick={() => handleScroll('left')}
          disabled={!canScrollLeft}
        >
          <svg width="16" height="16" viewBox="0 0 24 24">
            <path d="M15.41 16.59L10.83 12l4.58-4.59L14 6l-6 6 6 6 1.41-1.41z" />
          </svg>
        </button>

        {/* 标签导航区域 */}
        <div className="tabs-nav" ref={navRef}>
          {React.Children.map(children, (item, idx) => {
            const {
              key,
              props: { tab, info },
            } = item;
            const active = `${activeKey}` === key;
            return (
              <div
                key={`item-${idx}`}
                className={classNames('nav-item', key, { active })}
                onClick={() => {
                  onChange(parseInt(key, 10));
                }}
              >
                {tab}
                {info}
              </div>
            );
          })}
        </div>

        {/* 右侧滚动按钮 */}
        <button
          className={`scroll-button ${canScrollRight ? '' : 'disabled'}`}
          onClick={() => handleScroll('right')}
          disabled={!canScrollRight}
        >
          <svg width="16" height="16" viewBox="0 0 24 24">
            <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z" />
          </svg>
        </button>
      </div>

      {childLen > 1 ? children.filter((item: any) => item.key === String(activeKey)) : children}
    </div>
  );
};

Tabs.TabPane = TabPane;

Tabs.defaultProps = {
  onChange: () => {},
};

export default Tabs;